import {
  Activity,
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  CalendarCheck,
  CalendarClock,
  ChartNoAxesCombined,
  ClipboardCheck,
  ClipboardList,
  Database,
  Heart,
  Home,
  ListChecks,
  PanelsTopLeft,
  LogOut,
  ScrollText,
  ShieldCheck,
  UserRound,
  UserRoundCheck,
  Users,
  WalletCards,
} from "lucide-react";
import type { ReactNode } from "react";
import { hasPermission, type Permission } from "@web/config/permissions";
import type { UserRole } from "@web/config/roles";

export const menuPermission = (item: Pick<NavItem, "path" | "action">) =>
  `menu:${(item.path || item.action || "").replace(/^\//, "").replaceAll("/", "_").replaceAll("-", "_")}` as const;

export interface NavItem {
  label: string;
  section: string;
  path?: string;
  action?: "logout";
  icon: ReactNode;
  permission: Permission;
  hidden?: boolean;
}

const icon = (Icon: typeof Home) => <Icon size={20} />;
export const navigation: NavItem[] = [
  {
    label: "Organization",
    section: "TEAM MANAGEMENT",
    path: "/organization",
    icon: icon(Users),
    permission: "users:view",
  },
  {
    label: "Sales Structure",
    section: "TEAM MANAGEMENT",
    path: "/sales-reporting",
    icon: icon(Users),
    permission: "leads:view",
  },
  {
    label: "Dashboard",
    section: "MAIN",
    path: "/dashboard",
    icon: icon(Home),
    permission: "dashboard:view",
  },
  {
    label: "Calendar",
    section: "MAIN",
    path: "/calendar",
    icon: icon(CalendarDays),
    permission: "calendar:view",
  },
  {
    label: "Team Leads",
    section: "LEAD MANAGEMENT",
    path: "/leads",
    icon: icon(ClipboardList),
    permission: "leads:view",
  },
  {
    label: "Follow-Ups",
    section: "LEAD MANAGEMENT",
    path: "/follow-ups",
    icon: icon(CalendarClock),
    permission: "followups:view",
  },
  {
    label: "Pipeline",
    section: "LEAD MANAGEMENT",
    path: "/pipeline",
    icon: icon(PanelsTopLeft),
    permission: "pipeline:view",
    hidden: true,
  },
  {
    label: "Meetings / Site Visits",
    section: "LEAD MANAGEMENT",
    path: "/meetings-site-visits",
    icon: icon(CalendarCheck),
    permission: "followups:view",
    hidden: true,
  },
  {
    label: "KYC Approvals",
    section: "LEAD MANAGEMENT",
    path: "/kyc-management",
    icon: icon(ShieldCheck),
    permission: "kyc:review",
  },
  {
    label: "Properties",
    section: "PROPERTY MANAGEMENT",
    path: "/properties",
    icon: icon(Building2),
    permission: "properties:view",
  },
  {
    label: "Listing Requests",
    section: "PROPERTY MANAGEMENT",
    path: "/tickets",
    icon: icon(ScrollText),
    permission: "tickets:view",
  },
  {
    label: "Team Members",
    section: "TEAM MANAGEMENT",
    path: "/team-management",
    icon: icon(Users),
    permission: "users:view",
  },
  {
    label: "Lead Assignments",
    section: "TEAM MANAGEMENT",
    path: "/lead-assignments",
    icon: icon(UserRoundCheck),
    permission: "users:view",
    hidden: true,
  },
  {
    label: "Team Performance",
    section: "TEAM MANAGEMENT",
    path: "/team-performance",
    icon: icon(ChartNoAxesCombined),
    permission: "reports:view",
    hidden: true,
  },
  {
    label: "Reports",
    section: "REPORTS",
    path: "/reports",
    icon: icon(BarChart3),
    permission: "report_daily:view",
  },
  {
    label: "Report All",
    section: "REPORTS",
    path: "/report-all",
    icon: icon(BarChart3),
    permission: "report_all:view",
  },
  {
    label: "Sales & Revenue",
    section: "REPORTS",
    path: "/reports/sales-revenue",
    icon: icon(WalletCards),
    permission: "report_revenue:view",
  },
  {
    label: "Agent Performance",
    section: "REPORTS",
    path: "/reports/agent-performance",
    icon: icon(ChartNoAxesCombined),
    permission: "report_performance:view",
  },
  {
    label: "My Profile",
    section: "ACCOUNT",
    path: "/profile",
    icon: icon(UserRound),
    permission: "profile:view",
  },
  {
    label: "Logout",
    section: "ACCOUNT",
    action: "logout",
    icon: icon(LogOut),
    permission: "profile:view",
  },
  {
    label: "Dashboard",
    section: "MAIN",
    path: "/dashboard",
    icon: icon(Home),
    permission: "dashboard:view",
  },
  {
    label: "Assigned Leads",
    section: "MY LEADS",
    path: "/leads",
    icon: icon(ClipboardList),
    permission: "leads:view",
  },
  {
    label: "Follow-Ups",
    section: "MY LEADS",
    path: "/follow-ups",
    icon: icon(CalendarClock),
    permission: "followups:view",
  },
  {
    label: "Pipeline",
    section: "MY LEADS",
    path: "/pipeline",
    icon: icon(PanelsTopLeft),
    permission: "pipeline:view",
    hidden: true,
  },
  {
    label: "Meetings / Site Visits",
    section: "MY LEADS",
    path: "/meetings-site-visits",
    icon: icon(CalendarCheck),
    permission: "followups:view",
    hidden: true,
  },
  {
    label: "KYC Forms",
    section: "MY LEADS",
    path: "/kyc-forms",
    icon: icon(ShieldCheck),
    permission: "leads:view",
    hidden: true,
  },
  {
    label: "Property List",
    section: "PROPERTIES",
    path: "/properties",
    icon: icon(Building2),
    permission: "properties:view",
  },
  {
    label: "My Properties",
    section: "PROPERTIES",
    path: "/my-properties",
    icon: icon(ListChecks),
    permission: "properties:view",
  },
  {
    label: "Favorites",
    section: "PROPERTIES",
    path: "/favorites",
    icon: icon(Heart),
    permission: "favorites:view",
  },
  {
    label: "Tasks",
    section: "MY WORK",
    path: "/tasks",
    icon: icon(ClipboardCheck),
    permission: "profile:view",
    hidden: true,
  },
  {
    label: "Notifications",
    section: "MY WORK",
    path: "/notifications",
    icon: icon(Bell),
    permission: "profile:view",
    hidden: true,
  },
  {
    label: "My Performance",
    section: "MY WORK",
    path: "/my-performance",
    icon: icon(ChartNoAxesCombined),
    permission: "profile:view",
    hidden: true,
  },
  {
    label: "My Profile",
    section: "ACCOUNT",
    path: "/profile",
    icon: icon(UserRound),
    permission: "profile:view",
  },
  {
    label: "Logout",
    section: "ACCOUNT",
    action: "logout",
    icon: icon(LogOut),
    permission: "profile:view",
  },
  {
    label: "Roles & Permissions",
    section: "SYSTEM",
    path: "/roles",
    icon: icon(ShieldCheck),
    permission: "roles:view",
  },
  {
    label: "My Profile",
    section: "ACCOUNT",
    path: "/profile",
    icon: icon(UserRound),
    permission: "profile:view",
  },
  {
    label: "Logout",
    section: "ACCOUNT",
    action: "logout",
    icon: icon(LogOut),
    permission: "profile:view",
  },
  {
    label: "Master Data",
    section: "SYSTEM",
    path: "/master-data",
    icon: icon(Database),
    permission: "master:view",
  },
  {
    label: "Activity Logs",
    section: "SYSTEM",
    path: "/activity-logs",
    icon: icon(Activity),
    permission: "logs:view",
  },
  {
    label: "Error Monitoring",
    section: "SYSTEM",
    path: "/api-logs",
    icon: icon(Activity),
    permission: "logs:view",
  },
];

export function navigationForUser(user: {
  role: UserRole;
  roles?: readonly UserRole[];
  permissions?: readonly string[];
}) {
  const organizationScope = Boolean(
    user.permissions?.includes("access:organization"),
  );
  const permitted = navigation.filter(
    (item) =>
      !item.hidden &&
      hasPermission(user.role, item.permission, user.permissions) &&
      hasPermission(user.role, menuPermission(item), user.permissions),
  );
  const seen = new Set<string>();
  const items = permitted
    .filter((item) => {
      const key = item.path || `${item.section}:${item.action}:${item.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((item) =>
      item.path === "/leads"
        ? {
            ...item,
            label: user.permissions?.includes("leads:view_all")
              ? "Company Leads"
              : user.permissions?.includes("leads:view_team")
                ? "Team Leads"
                : "Assigned Leads",
            section: organizationScope ? "LEAD MANAGEMENT" : "MY LEADS",
          }
        : item,
    );
  const calendarIndex = items.findIndex((item) => item.path === "/calendar");
  const calendar =
    calendarIndex >= 0 ? items.splice(calendarIndex, 1)[0] : undefined;
  if (calendar) {
    const dashboardIndex = items.findIndex(
      (item) => item.path === "/dashboard",
    );
    items.splice(dashboardIndex + 1, 0, calendar);
  }
  const sectionOrder = organizationScope
    ? [
        "MAIN",
        "LEAD MANAGEMENT",
        "PROPERTY MANAGEMENT",
        "TEAM MANAGEMENT",
        "REPORTS",
        "SYSTEM",
        "PROPERTIES",
        "ACCOUNTS",
      ]
    : [
        "MAIN",
        "MY LEADS",
        "PROPERTIES",
        "TEAM MANAGEMENT",
        "REPORTS",
        "ACCOUNTS",
      ];
  const orderedItems = items.map((item) => ({
    ...item,
    section:
      item.section === "ACCOUNT"
        ? "ACCOUNTS"
        : !organizationScope && item.section === "LEAD MANAGEMENT"
          ? "MY LEADS"
          : !organizationScope && item.section === "PROPERTY MANAGEMENT"
            ? "PROPERTIES"
            : item.section,
  }));
  const sectionRank = (section: string) => {
    const index = sectionOrder.indexOf(section);
    return index >= 0 ? index : sectionOrder.length - 1.5;
  };
  return orderedItems.sort(
    (a, b) => sectionRank(a.section) - sectionRank(b.section),
  );
}
